import React, { FC, useState } from 'react';

import PropTypes from 'prop-types';

import EmptyState from '@adsk/alloy-react-empty-state';
import { useLocale } from '@adsk/alloy-react-locale';
import { SimpleModal } from '@adsk/alloy-react-modal';
import ProgressRing from '@adsk/alloy-react-progress-ring';

import {
  bodyStyle,
  subscriptionsContainer,
  ErrorLabel,
  loadingSpinnerContainerStyles,
} from './helpers/styles';
import { Offering } from '../../helpers/types';
import SubscriptionItem from '../SubscriptionItem/index';

interface SubscriptionsModalProps {
  onClose: () => void;
  subscriptions: Offering[];
  error: boolean;
  isLoading: boolean;
  onClickTryAgain: () => void;
}
interface ExpandedItem {
  [key: string]: boolean;
}

const SubscriptionsModal: FC<SubscriptionsModalProps> = ({
  onClose,
  subscriptions,
  error,
  isLoading,
  onClickTryAgain,
}) => {
  // First item is always expanded
  const [expandedItems, setExpandedItems] = useState<ExpandedItem>({ 0: true });
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const hasSubscriptions = subscriptions?.length > 0;

  const LoadingSpinner = () => {
    return (
      <div
        data-testid="SubscriptionsModal__LoadingSpinner"
        style={loadingSpinnerContainerStyles}
      >
        <ProgressRing />
      </div>
    );
  };

  const EmptyStateLabel = () => {
    if (error) {
      return (
        <>
          <ErrorLabel data-testid="SubscriptionModal_EmptyState_Label">
            {T.translate('FETCH_SUBSCRIPTIONS_ERROR')}
          </ErrorLabel>
          <a onClick={onClickTryAgain} style={{ cursor: 'pointer' }}>
            {T.translate('TRY_AGAIN')}
          </a>
        </>
      );
    }
    return (
      <ErrorLabel data-testid="SubscriptionModal_ErrorState_Label">
        {T.translate('NO_SUBSCRIPTIONS_ACTIVE')}
      </ErrorLabel>
    );
  };

  const ModalContent = () => {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (hasSubscriptions && !error) {
      return (
        <div style={subscriptionsContainer}>
          {subscriptions.map((subscription: Offering, index) => {
            return (
              <SubscriptionItem
                key={`${subscription.offeringName}_${index}`}
                subscription={subscription}
                expanded={expandedItems[index]}
                onExpand={() => {
                  const newExpand = {
                    ...expandedItems,
                    [index]: !expandedItems[index],
                  };

                  setExpandedItems(newExpand);
                }}
              />
            );
          })}
        </div>
      );
    }
    return (
      <EmptyState
        data-testid="SubscriptionsModal__EmptyState"
        illustrationType="buildingConstructionGrey"
      >
        <EmptyStateLabel />
      </EmptyState>
    );
  };

  return (
    <SimpleModal
      title={T.translate('MY_SUBSCRIPTIONS')?.toString()}
      onClose={onClose}
      show
      modalBodyProps={{ style: bodyStyle }}
    >
      <ModalContent />
    </SimpleModal>
  );
};

SubscriptionsModal.displayName = 'SubscriptionsModal';
SubscriptionsModal.propTypes = {
  /** Handler for close modal event */
  onClose: PropTypes.func.isRequired,
  /**  User subscriptions and products*/
  subscriptions: PropTypes.array.isRequired,
  error: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onClickTryAgain: PropTypes.func.isRequired,
};
export default SubscriptionsModal;
